import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randRecentDate,
  randNumber,
  randChanceBoolean,
  randProductName,
} from '@ngneat/falso'

import type { SchemaAttributes } from '../../types.js'
import type { MockServer } from '../server.js'
import { getExistingEntity } from './../utils.js'

type ConsultationFood = SchemaAttributes<'ConsultationFood'>

export const model = (Model as ModelDefinition<ConsultationFood>).extend({
  stockLocation: belongsTo('stockLocation'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  supervisingVeterinarian: belongsTo('user'),
  performedBy: belongsTo('user'),
  patient: belongsTo('patient'),
})

export const factory = Factory.extend<ConsultationFood>({
  code() {
    return 'FOOD-'.concat(String(randNumber({ min: 1000, max: 9999 })))
  },
  name() {
    return randProductName()
  },
  usage_type() {
    return rand(['ADMINISTERED', 'PRESCRIPTION', 'GIVEN', 'HOME_DELIVERY'])
  },
  used() {
    return randChanceBoolean({ chanceTrue: 0.8 })
      ? randRecentDate().toISOString()
      : null
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },
  herd_size_multiplier() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? randNumber({ min: 1, max: 10 })
      : null
  },
  display_price() {
    return randNumber({ min: 10, max: 500, precision: 2 })
  },
  hidden_items_display_price() {
    return randChanceBoolean({ chanceTrue: 0.2 })
      ? randNumber({ min: 5, max: 50, precision: 2 })
      : 0
  },
  is_minimum_charge_active() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  is_cabinet_item() {
    return randChanceBoolean({ chanceTrue: 0.5 })
  },
  display_amount() {
    return randNumber({ min: 1, max: 10, precision: 1 })
  },
  display_size() {
    const sizes = ['g', 'kg', 'ml']
    return rand(sizes)
  },
  instructions() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? 'Feed twice daily with meals'
      : null
  },

  afterCreate(food, server) {
    if (!food.stockLocation) {
      const stockLocation = server.schema.first('stockLocation')
      if (stockLocation) {
        food.update({ stockLocation })
      }
    }

    if (!food.createdUser) {
      const createdUser = getExistingEntity(server, 'user')
      food.update({ createdUser })
    }

    if (!food.modifiedUser) {
      const modifiedUser = getExistingEntity(server, 'user')
      food.update({ modifiedUser })
    }
  },
})

export function routes(server: MockServer) {
  server.get('/consultation_foods/:id', (schema, request) => {
    const { id } = request.params
    return schema.find('consultationFood', id)
  })
}
